import { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { RootState } from 'common/redux/core/root.reducer';
import { DataList, MessageGuide } from 'common/components';
import {
    ACTION_ITEMS,
    PAGE_NUMBER_DEFAULT,
    PAGE_SIZE_DEFAULT,
    defaultPageRequest
} from 'common/config';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { Action, ApiStatus } from 'common/enums';

import {
    COMPANY_FOOTPRINT_GUIDE,
    COMPANY_FOOTPRINT_TITLE,
    getCompanyFootprintColDefs
} from 'modules/data-management/config';
import { UpdateFootprintPayload } from 'modules/data-management/models/data-management.model';
import CompanyFootprintAddEdit from 'modules/data-management/components/company-footprint-add-edit/CompanyFootprintAddEdit';
import { getCompanyFootprintResponse } from 'modules/data-management/redux/data-management.selector';
import { dataManagementActions } from 'modules/data-management/redux';
import ConfigurationHeader from 'modules/configuration/components/configuration-header/ConfigurationHeader';
import { searchChangeHandlerUtil } from 'modules/data-management/utils/company-footprint.utils';
import { GridApi } from 'ag-grid-community/dist/lib/gridApi';
import useCompanyFootprintAccess from 'modules/data-management/roles-access-hooks/useCompanyFootprintAccess';

const CompanyFootprint: FC = () => {
    const [searchVal, setSearchVal] = useState<string>('');
    const [isinfoModalVisible, setInfoModalVisible] = useState(false);
    const [pagedRequest, setPagedRequest] = useState<PagedRequest>(defaultPageRequest);
    const companyFootprintResponse = useSelector((state: RootState) =>
        getCompanyFootprintResponse(state)
    );
    const dispatch = useDispatch();
    const timer = useRef<null | ReturnType<typeof setTimeout>>(null);

    const { isSetUpDisabled } = useCompanyFootprintAccess();

    const onSearchChangeHandler = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        /** searchChangeHandlerUtil has debounce and sets the pagedRequest so that useEffect runs */
        searchChangeHandlerUtil(event, setSearchVal, setPagedRequest, timer);
    }, []);

    const onEdit = () => {
        setInfoModalVisible(true);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleAction = (action: Action, data: any): void => {
        if (action === Action.ToggleCompanyPresence) {
            const updatedData: UpdateFootprintPayload = {
                userCompanyLocationId: data.userCompanyLocationId,
                isEYPresence: !data.isEYPresence
            };
            dispatch(dataManagementActions.updateCompanyFootprint(updatedData));
        }
    };

    const handleScroll = () => {
        if (
            companyFootprintResponse.status !== ApiStatus.LOADING &&
            companyFootprintResponse &&
            companyFootprintResponse?.data &&
            companyFootprintResponse?.data?.pagination &&
            companyFootprintResponse?.data?.pagination.totalPages >
                companyFootprintResponse?.data?.pagination.currentPage
        ) {
            const updatedRequest: PagedRequest = {
                ...pagedRequest,
                pageNumber: pagedRequest.pageNumber + 1,
                searchText: searchVal
            };
            setPagedRequest(updatedRequest);
        }
    };

    const handleEditPopupClose = () => {
        setInfoModalVisible(false);
    };

    useEffect(() => {
        dispatch(dataManagementActions.fetchCompanyFootprint(pagedRequest));
    }, [dispatch, pagedRequest]);

    return (
        <div className="route-container">
            <ConfigurationHeader
                headerTitle={COMPANY_FOOTPRINT_TITLE}
                buttonTitle={ACTION_ITEMS.EDIT_BUTTON}
                searchVal={searchVal}
                isButtonVisible={true}
                hideClearButton={false}
                disableButton={isSetUpDisabled}
                onSearchChangeHandler={onSearchChangeHandler}
                onAddNew={onEdit}
            />
            <MessageGuide message={COMPANY_FOOTPRINT_GUIDE} />
            <DataList
                isNoDataVisible={companyFootprintResponse?.data?.data?.length === 0}
                data={companyFootprintResponse?.data?.data || []}
                columnDefs={getCompanyFootprintColDefs(handleAction, isSetUpDisabled)}
                fetchNextDataSet={handleScroll}
                suppressDrag={searchVal.length > 0}
            />
            {isinfoModalVisible && (
                <CompanyFootprintAddEdit
                    isVisible={isinfoModalVisible}
                    onClose={handleEditPopupClose}
                />
            )}
        </div>
    );
};
export default CompanyFootprint;
